import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

// ── Icons (เส้นบาง แทน @mui/icons-material) ────────────────────────────────────────────────
const PATHS = {
  moon: <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />,
  sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
  retry: <><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /></>,
  plus: <path d="M12 5v14M5 12h14" />,
  play: <path d="M7 4v16l13-8z" />,
  edit: <path d="M4 20h4L19 9l-4-4L4 16z" />,
  trash: <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" />,
  upload: <><path d="M12 16V4M7 9l5-5 5 5" /><path d="M4 16v4h16v-4" /></>,
  download: <><path d="M12 4v12M7 11l5 5 5-5" /><path d="M4 20h16" /></>,
  mail: <><rect x="3" y="5" width="18" height="14" rx="1" /><path d="m3 7 9 6 9-6" /></>,
  copy: <><rect x="8" y="8" width="12" height="12" rx="1" /><path d="M16 8V4H4v12h4" /></>,
  move: <path d="M5 12h14M13 6l6 6-6 6" />,
  clip: <path d="M21 11 12 20a6 6 0 0 1-8.5-8.5l9-9a4 4 0 0 1 5.7 5.7l-9 9a2 2 0 0 1-2.8-2.8L15 6" />,
  x: <path d="M6 6l12 12M18 6 6 18" />,
  eye: <><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></>,
  refresh: <><path d="M20 11A8 8 0 0 0 6.3 5.7L4 8" /><path d="M4 4v4h4" /><path d="M4 13a8 8 0 0 0 13.7 5.3L20 16" /><path d="M20 20v-4h-4" /></>,
};

export function Icon({ name, size = 16 }) {
  return (
    <svg className="i" viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">{PATHS[name]}</svg>
  );
}

// ── Modal (แทน MUI Dialog) ────────────────────────────────────────────────────────────────
export function Modal({ open, title, onClose, children, actions, wide = false, busy = false }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, busy]);
  if (!open) return null;
  return (
    <div className="modal" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className={`modal-box${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} disabled={busy} aria-label="ปิด"><Icon name="x" /></button>
        </div>
        <div className="modal-body">{children}</div>
        {actions && <div className="modal-acts">{actions}</div>}
      </div>
    </div>
  );
}

// ── Toast ─────────────────────────────────────────────────────────────────────────────────
const ToastContext = createContext(() => {});

export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);
  const push = useCallback((msg, kind = 'ok') => {
    const id = Math.random().toString(36).slice(2);
    setItems((prev) => [...prev, { id, msg, kind }]);
    setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), kind === 'error' ? 6000 : 3500);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toast-stack" role="status" aria-live="polite">
        {items.map((t) => <div key={t.id} className={`toast${t.kind === 'error' ? ' error' : ''}`}>{t.msg}</div>)}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}

// ── Switch ────────────────────────────────────────────────────────────────────────────────
export function Switch({ checked, onChange, label, disabled }) {
  return (
    <label className="switch" title={label}>
      <input type="checkbox" checked={!!checked} disabled={disabled} aria-label={label}
        onChange={(e) => onChange(e.target.checked)} />
      <span />
    </label>
  );
}

// ── Pager ─────────────────────────────────────────────────────────────────────────────────
export function Pager({ page, pages, onPage }) {
  if (pages <= 1) return null;
  const nums = [];
  for (let i = 0; i < pages; i += 1) {
    if (i === 0 || i === pages - 1 || Math.abs(i - page) <= 2) nums.push(i);
    else if (nums[nums.length - 1] !== '…') nums.push('…');
  }
  return (
    <div className="pager">
      <button disabled={page === 0} onClick={() => onPage(page - 1)} aria-label="หน้าก่อน">‹</button>
      {nums.map((n, i) => (n === '…'
        ? <span key={`e${i}`}>…</span>
        : <button key={n} className={n === page ? 'on' : ''} onClick={() => onPage(n)}>{n + 1}</button>))}
      <button disabled={page >= pages - 1} onClick={() => onPage(page + 1)} aria-label="หน้าถัดไป">›</button>
    </div>
  );
}

// ── TUN picker (แทน MUI Autocomplete — รายการมี ~575 TUN จึงแสดงแค่ 50 ตัวแรกที่ตรง) ─────────
const PICK_LIMIT = 50;

export function TunPicker({ options, value, onChange, placeholder = 'พิมพ์รหัส TUN…', id }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hl, setHl] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const matches = useMemo(() => {
    const s = q.trim().toUpperCase();
    const pool = options.filter((t) => !value.includes(t));
    if (!s) return pool;
    const starts = pool.filter((t) => t.toUpperCase().startsWith(s));
    const contains = pool.filter((t) => !t.toUpperCase().startsWith(s) && t.toUpperCase().includes(s));
    return [...starts, ...contains];
  }, [q, options, value]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const add = (t) => { onChange([...value, t]); setQ(''); setHl(0); inputRef.current?.focus(); };
  const remove = (t) => onChange(value.filter((x) => x !== t));

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHl((h) => Math.min(h + 1, Math.min(matches.length, PICK_LIMIT) - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHl((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (open && matches[hl]) add(matches[hl]); }
    else if (e.key === 'Backspace' && !q && value.length) remove(value[value.length - 1]);
    // ปิดแค่รายการ ไม่ให้ Escape ทะลุไปปิด Modal ที่ห่อ picker อยู่ (ฟอร์มตารางส่ง)
    else if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); }
  };

  return (
    <div className="tunpick" ref={wrapRef}>
      <div className="tunpick-box" onClick={() => inputRef.current?.focus()}>
        {value.map((t) => (
          <span className="t" key={t}>{t}<button type="button" aria-label={`เอา ${t} ออก`} onClick={(e) => { e.stopPropagation(); remove(t); }}>×</button></span>
        ))}
        <input id={id} ref={inputRef} value={q} placeholder={value.length ? '' : placeholder}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setHl(0); }}
          onFocus={() => setOpen(true)} onKeyDown={onKey} autoComplete="off" aria-label="เพิ่ม TUN" />
      </div>
      {open && matches.length > 0 && (
        <div className="tunpick-list" role="listbox">
          {matches.slice(0, PICK_LIMIT).map((t, i) => (
            <button type="button" key={t} className={i === hl ? 'hl' : ''} onMouseEnter={() => setHl(i)}
              onMouseDown={(e) => { e.preventDefault(); add(t); }}>{t}</button>
          ))}
          {matches.length > PICK_LIMIT && <div className="more">อีก {matches.length - PICK_LIMIT} TUN — พิมพ์เพิ่มเพื่อกรอง</div>}
        </div>
      )}
    </div>
  );
}

// ── Formatting ────────────────────────────────────────────────────────────────────────────
export const fmtNum = (n) => (Number(n) || 0).toLocaleString('en-US');

export function fmtSize(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const pad = (n) => String(n).padStart(2, '0');

// epoch วินาที → "10/10 14:31" (ปีนี้) หรือ "10/10/26 14:31"
export function fmtEpoch(sec) {
  if (!sec) return '';
  const d = new Date(sec * 1000);
  const now = new Date();
  const date = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}${d.getFullYear() === now.getFullYear() ? '' : `/${String(d.getFullYear()).slice(2)}`}`;
  return `${date} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function fmtIso(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return fmtEpoch(d.getTime() / 1000);
}

export function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const TH_DAYS = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'];
const TH_MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];

// "2026-10-10" → "ศุกร์ 10 ต.ค. 2569"
export function thaiDate(key) {
  const [y, m, d] = key.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return `${TH_DAYS[dt.getDay()]} ${d} ${TH_MONTHS[m - 1]} ${y + 543}`;
}
